import { useFocusEffect } from '@react-navigation/native';
import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import {
    View,
    Text,
    StyleSheet,
    FlatList,
    TouchableOpacity,
    RefreshControl,
    useWindowDimensions
} from 'react-native';
import { ZenEmptyComponent, ZenIcon, ZenLoader, ZenText } from 'zen-core-elements';
import { useAppRoute } from '../../../types/@react-navigation/navigation';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { getPosCustomerHistory } from '../../../redux/slices/customerSlice';
import { config } from '../../../shared/environment';
import CommonService from '../../../shared/services/commonService';
import { ThemeConsumer } from '@rneui/themed';
import { loyaltyDetails, onAccountDetails } from '../customerHistoryModule';
import moment from "moment";


const HISTORY_DATA = [
    { id: '1', type: 'account', title: 'Balance Increase', date: 'Jan 10, 2026', time: '10:30 AM', amount: '30.00', balance: '100', action: 'credit' },
    { id: '2', type: 'loyalty', title: 'Incremented by Admin', date: 'Jul 24, 2025', time: '11:45 AM', amount: '500', action: 'credit' },
    { id: '3', type: 'loyalty', title: 'Incremented by Admin', date: 'Jul 24, 2025', time: '11:45 AM', amount: '500', action: 'credit' },
    { id: '4', type: 'order', title: 'POS Order #8821', date: 'Dec 05, 2024', time: '02:15 PM', amount: '1,200.00', action: 'debit' },
];

const StatCard = ({ label, value, icon, color }: { label: string, value: string, icon: ReactNode, color: string }) => (
    <ThemeConsumer>
        {({ theme }) => (
            <View style={styles.statCard}>
                <View style={[styles.statIconBox, { backgroundColor: color + '20' }]}>
                    {icon}
                </View>
                <View style={{ flex: 1 }}>
                    <ZenText content={label} style={{ fontFamily: theme?.medium, color: theme?.grey, fontSize: theme?.errorFontSize, textTransform: "uppercase" }} />
                    <ZenText content={value} style={{ fontFamily: theme?.bold, color: theme?.lightDark, fontSize: theme?.smallerFontSize, }} />
                </View>
            </View>
        )}
    </ThemeConsumer >
);

const HistoryItem = ({ item }: { item: onAccountDetails & loyaltyDetails }) => {
    const currencySymbol = useAppSelector((state) => state?.common?.currentUser?.value?.purchasedCurrency?.currencySymbol) ?? '₹'
    const isCredit = item.amount != undefined ? true : false

    const color = isCredit ? '#10B981' : '#64748B';
    const bgColor = isCredit ? '#ECFDF5' : '#F1F5F9';

    return (
        <ThemeConsumer>
            {({ theme }) => (
                <View style={styles.itemContainer}>
                    <View style={[styles.itemIcon, { backgroundColor: bgColor }]}>
                        {isCredit ? <ZenIcon type='antdesign' name='creditcard' size={20} color={color} /> : <ZenIcon type='antdesign' name="wallet" size={20} color={color} />}
                    </View>

                    <View style={styles.itemContent}>
                        <Text style={[styles.itemTitle, { fontFamily: theme?.bold, fontSize: theme?.smallFontSize, color: theme?.lightDark }]}>{item.activity || item?.message}</Text>
                        {item.createdAt ? <View style={styles.itemMetaRow}>
                            <ZenIcon type='antdesign' name='calendar' size={12} color="#94A3B8" />
                            <Text style={{
                                fontFamily: theme?.medium,
                                fontSize: theme?.badgeFontSize,
                                color: theme?.grey
                            }}> {moment(item.createdAt).format("DD MMM YYYY")} • {moment(item.createdAt).format("hh:mm A")}</Text>
                        </View> : <></>}
                    </View>

                    <View style={styles.itemRight}>
                        <Text style={[styles.itemAmount, { fontFamily: theme?.bold, fontSize: theme?.smallFontSize, color: (isCredit || item?.event != "Reduced") ? '#15803D' : theme?.lightDark }]}>
                            {isCredit ? '+' : item?.event == "Reduced" ? "-" : '+'}{isCredit ? CommonService?.currencyStandard(currencySymbol, item?.amount?.toString() || "") : item.points + ' pts'}
                        </Text>
                        <Text style={[styles.itemBadge, {
                            fontFamily: theme?.medium, fontSize: theme?.badgeFontSize, color: theme?.grey,
                            backgroundColor: theme?.lightBackgroundColor
                        }]}>{isCredit ? 'ACCOUNT' : 'LOYALTY'}</Text>
                    </View>
                </View>
            )}
        </ThemeConsumer>
    );
};

export default function POSHistoryTab({ customerId }: { customerId: number }) {
    //variable which holds route data
    const route = useAppRoute()
    // Live window width (was read once at module load, so resizing/rotating kept the old layout)
    const isTablet = useWindowDimensions().width > 768
    //variable to store redux dispatch
    const dispatch = useAppDispatch()
    const [activeFilter, setActiveFilter] = useState('All');
    const [isSeen, setIsSeen] = useState(false)
    const [listLoader, setListLoader] = useState(true)
    //variable to handle list refresh state
    const [listRefresh, setListRefresh] = useState(false)
    const { value: posCustomerHistory } = useAppSelector((state) => state?.customer?.posCustomerHistory)
    const currencySymbol = useAppSelector((state) => state?.common?.currentUser?.value?.purchasedCurrency?.currencySymbol) ?? '₹'

    const SUMMARY_STATS = {
        totalSpend: CommonService.currencyStandard(currencySymbol, "362448.20"),
        totalOrders: '19',
        lastOrder: 'Jan 10, 2026'
    };

    //Funtion to get a pos customer history data
    const getPosCustomerHistoryApi = async () => {
        await dispatch(getPosCustomerHistory({ storeId: config?.storeId, customerId })).then((response) => {
            if (response) {
                setListRefresh(false)
                setListLoader(false)
            }
        }).catch((error) => {
            console?.log("Get customer history details error", error)
        })
    }

    //useFocusEffect hook for getting current focusing page    
    useFocusEffect(
        useCallback(() => {
            if (!isSeen) {
                setIsSeen(true)
                setListLoader(true)
                getPosCustomerHistoryApi()
            }
        }, [route, isSeen]))

    const renderHeader = () => (
        <ThemeConsumer>
            {({ theme }) => (
                <View style={styles.headerContainer}>

                    <View style={[styles.statsGrid, { flexDirection: isTablet ? 'row' : 'column' }]}>
                        <StatCard
                            label="Total Spend"
                            value={CommonService?.currencyStandard(currencySymbol, posCustomerHistory?.totalamount?.toString() || "")}
                            icon={<ZenIcon type='antdesign' name="wallet" size={24} color="#0EA5E9" />}
                            color="#0EA5E9"
                        />
                        <StatCard
                            label="Total Orders"
                            value={posCustomerHistory?.ordercount || "0"}
                            icon={<ZenIcon type='feather' name='shopping-bag' size={24} color="#F59E0B" />}
                            color="#F59E0B"
                        />
                    </View>

                    <View style={styles.filterRow}>
                        {['All', 'Loyalty', 'Account'].map((filter) => (
                            <TouchableOpacity
                                key={filter}
                                style={[
                                    styles.filterChip,
                                    { backgroundColor: theme?.lightGrey },
                                    activeFilter === filter && { backgroundColor: theme?.primary }
                                ]}
                                onPress={() => setActiveFilter(filter)}
                            >
                                <Text style={[
                                    {
                                        fontSize: theme?.errorFontSize,
                                        fontFamily: theme?.medium,
                                        color: theme?.grey
                                    },
                                    activeFilter === filter && { color: theme?.light }
                                ]}>
                                    {filter}
                                </Text>
                            </TouchableOpacity>
                        ))}
                    </View>

                    <Text style={{
                        fontSize: theme?.smallerFontSize,
                        fontFamily: theme?.bold,
                        color: theme?.lightDark,
                        marginBottom: 5,
                    }}>Timeline</Text>
                </View>
            )}
        </ThemeConsumer>
    );
    const selectedFilter: any = useMemo(() => {
        if (activeFilter == "All") {
            return [...(posCustomerHistory?.loyaltyDetails ?? []), ...(posCustomerHistory?.onAccountDetails ?? [])]
        }
        else if (activeFilter == "Loyalty") {
            return (posCustomerHistory?.loyaltyDetails || [])
        } else {
            return (posCustomerHistory?.onAccountDetails || [])
        }
    }, [activeFilter, posCustomerHistory?.loyaltyDetails, posCustomerHistory?.onAccountDetails])

    const onRefresh = () => {
        setListRefresh(true)
        getPosCustomerHistoryApi()
    }
    return (
        <View style={styles.screenContainer}>
            {(listLoader && !listRefresh) ?
                <ZenLoader lottie={require('../../../assets/infinityLoader.json')} lottieType='Local' viewStyle={{ backgroundColor: "transparent" }} /> :
                <FlatList
                    data={selectedFilter}
                    keyExtractor={(_, index) => index?.toString()}
                    renderItem={({ item }) => <HistoryItem item={item} />}
                    ListHeaderComponent={renderHeader}
                    contentContainerStyle={styles.listContent}
                    showsVerticalScrollIndicator={false}
                    ListEmptyComponent={
                        <ZenEmptyComponent viewStyle={{ backgroundColor: "transparent" }} />
                    }
                    refreshControl={<RefreshControl refreshing={listRefresh} onRefresh={onRefresh} />}
                />
            }
        </View>
    );
}

const styles = StyleSheet.create({
    screenContainer: {
        flex: 1,
    },
    listContent: {
        paddingBottom: 40,
    },
    headerContainer: {
        padding: 16,
    },
    statsGrid: {
        gap: 12,
        marginBottom: 24,
    },
    statCard: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#FFFFFF',
        padding: 16,
        borderRadius: 16,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.05,
        shadowRadius: 8,
        elevation: 2,
    },
    statIconBox: {
        width: 48,
        height: 48,
        borderRadius: 12,
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: 12,
    },
    filterRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 16,
    },
    filterChip: {
        paddingVertical: 8,
        paddingHorizontal: 20,
        borderRadius: 100,
        marginRight: 8,
    },
    filterIconBtn: {
        marginLeft: 'auto',
        padding: 8,
    },
    itemContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#FFFFFF',
        marginHorizontal: 16,
        marginBottom: 12,
        padding: 16,
        borderRadius: 12,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.03,
        shadowRadius: 4,
        elevation: 1,
    },
    itemIcon: {
        width: 40,
        height: 40,
        borderRadius: 10,
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: 12,
    },
    itemContent: {
        flex: 1,
    },
    itemTitle: {
        marginBottom: 4,
    },
    itemMetaRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    itemRight: {
        alignItems: 'flex-end',
    },
    itemAmount: {
        marginBottom: 4,
    },
    itemBadge: {
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: 4,
    }
});
